import { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Avatar,
  Chip,
  Stack,
  Tooltip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Switch,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import MinimizeRoundedIcon from "@mui/icons-material/MinimizeRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RoomRoundedIcon from "@mui/icons-material/RoomRounded";
import SvgIcon from "@mui/material/SvgIcon";
import { useNavigate } from "react-router-dom";
import { cryptoEncryptData } from "../../utils/helper";
import { fetchList } from "../../apis/services/CommonApiService";
import { ApiEndPoints } from "../../apis/ApiEndPoints";
import DialogForm from "./DialogForm";
import ReservationCheckIn from "../../pages/Reservation/ReservationCheckIn";
import ReservationCheckOut from "../../pages/Reservation/ReservationCheckOut";
import MakePaymentDialog from "../../pages/Reservation/MakePaymentDialog";
import Loader from "../../layout/Loader";
import { fetchReservationsPage, buildHotelInfoMessage, BOOKING_STATUS_OPTIONS } from "../../chatbot-admin/chatbotApi";
import ReactToPrint from "react-to-print";
import { QUICK_ACTIONS, REPORT_OPTIONS, matchIntent } from "../../chatbot-admin/intents";
import { REPORTS, reportSubtitle } from "../../chatbot-admin/reportRegistry";
import ReportDialog from "../../chatbot-admin/ReportDialog";
import { fetchRevenueBySource, fetchTransactionBatch, fetchOtaCommission } from "../../chatbot-admin/reportHandlers";
import ReportChartView from "../../chatbot-admin/ReportChartView";
import PeriodPicker from "../../chatbot-admin/PeriodPicker";

// The 3 chart reports use the chatbot shortcut-date picker (not the
// generic single/range form) so they feel like real chat conversations.
const CHART_REPORT_KEYS = new Set(["ADMIN_REVENUE_BY_SOURCE", "ADMIN_TRANSACTION_BATCH", "ADMIN_OTA_COMMISSION"]);

const CHART_REPORT_LABELS = {
  ADMIN_REVENUE_BY_SOURCE: "Revenue By Source",
  ADMIN_TRANSACTION_BATCH: "Transaction Batch Report",
  ADMIN_OTA_COMMISSION: "OTA Commission Report",
};

const WELCOME_MESSAGE = {
  id: "welcome",
  role: "bot",
  kind: "text",
  text: "Hello! How can I help you today?",
};
const FALLBACK_TYPED_MESSAGE = "I can help with Hotel details, Reservations and Reports (try \"Z - Out Report\") — or tap a quick action below.";

const OPEN_MS = 420;
const CLOSE_MS = 280;

// Launcher drag settings
const LAUNCHER = 62;
const EDGE = 16;
const POS_KEY = "admin-chat-launcher-pos";

// Chat window resize limits
const MIN_W = 400;
// Default window: roomy enough for tables/charts, small enough not to cover the page.
const DEFAULT_W = 520;
const DEFAULT_H = 620;
const MAX_W = 960; // width when maximised
const MIN_H = 440;
const HEADER_H = 72; // height of the header bar = size of the minimized window

const toLocalISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function todayISO() {
  return toLocalISO(new Date());
}
function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalISO(d);
}

// Keeps the launcher fully inside the viewport. x / y are offsets from the
// default bottom-right spot (0,0), so they are always <= EDGE and negative
// when moved left / up.
const clampPos = (x, y) => ({
  x: Math.min(EDGE, Math.max(-(window.innerWidth - EDGE - LAUNCHER), x)),
  y: Math.min(EDGE, Math.max(-(window.innerHeight - EDGE - LAUNCHER), y)),
});

function loadSavedPos() {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return { x: 0, y: 0 };
    const p = JSON.parse(raw);
    return clampPos(Number(p.x) || 0, Number(p.y) || 0);
  } catch {
    return { x: 0, y: 0 };
  }
}

/**
 * Manager-only chat widget.
 *
 * - The bell launcher is draggable anywhere on the page (position is remembered).
 * - When the chat opens, the launcher is hidden and the chat window opens
 *   at the bottom-right of the screen at its default size.
 * - The window has Minimize (header bar only) and Maximize buttons, plus a
 *   corner drag handle to resize it.
 * - When the chat closes, the launcher shows again where you left it.
 *
 * hotelDetails — pass the same raw object the admin panel already has
 * for this hotel (GET /hotel/hotelDetail/{id} shape).
 */
export default function AdminChatWidget({
  hotelDetails,
  hotelName = "Manager Assistant",
  primaryColor = "#0f172a",
  accentColor = "#2563eb",
}) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [reportDialog, setReportDialog] = useState({ open: false, key: null, params: {}, data: null, loading: false, error: "" });
  const [checkDialog, setCheckDialog] = useState({ open: false, mode: null, editedData: null, loading: false });
  const [suToken, setSuToken] = useState(null);
  const [suOn, setSuOn] = useState(false);
  const [isCashAllowed, setIsCashAllowed] = useState(0);
  const [openMakePaymentDialog, setOpenMakePaymentDialog] = useState(false);
  const [showPaymentMessage, setShowPaymentMessage] = useState(false);
  const [lastSearchFilters, setLastSearchFilters] = useState(null);
  const [pos, setPos] = useState(loadSavedPos); // launcher offset from default bottom-right spot
  // Window mode: "normal" (default size) | "maximized" | "minimized" (header bar only)
  const [mode, setMode] = useState("normal");
  const expanded = mode === "maximized";
  const minimized = mode === "minimized";
  const [size, setSize] = useState(null); // custom size from the corner handle: { w, h } | null
  const [resizing, setResizing] = useState(false);
  const paperRef = useRef(null);
  const resizeRef = useRef({ active: false, sx: 0, sy: 0, w: 0, h: 0 });
  const reportReq = useRef(0); // ignore stale responses
  const printRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const drag = useRef({ active: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });

  // New text -> follow the conversation to the bottom. A new report/reservation
  // result -> show it from its TOP (not its last row) and stay there.
  const lastSeenId = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const last = messages[messages.length - 1];
    const isNew = last?.id !== lastSeenId.current;
    lastSeenId.current = last?.id;
    if (last && (last.kind === "chartResult" || last.kind === "reservationList")) {
      if (isNew) {
        const node = el.querySelector(`[data-msg-id="${last.id}"]`);
        if (node) el.scrollTo({ top: Math.max(0, node.offsetTop - 8), behavior: "smooth" });
      }
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [messages, open, loading]);

  // Keep the window mounted long enough for the closing animation to play
  useEffect(() => {
    if (open) {
      setMounted(true);
    } else if (mounted) {
      const t = setTimeout(() => setMounted(false), CLOSE_MS);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Keep the launcher on screen when the browser window is resized
  useEffect(() => {
    const onResize = () => setPos((p) => clampPos(p.x, p.y));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Remember where the launcher was left
  useEffect(() => {
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(pos));
    } catch {
      /* storage unavailable — ignore */
    }
  }, [pos]);

  // ---- Launcher dragging --------------------------------------------
  const onDragStart = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    drag.current = { active: true, moved: false, sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
  };

  const onDragMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved) {
      if (Math.hypot(dx, dy) < 5) return; // below threshold = still a click
      d.moved = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    setPos(clampPos(d.ox + dx, d.oy + dy));
  };

  const onDragEnd = () => {
    drag.current.active = false;
  };

  const handleLauncherClick = () => {
    // a drag must not toggle the chat
    if (drag.current.moved) {
      drag.current.moved = false;
      return;
    }
    setMode("normal"); // always open at the default size
    setOpen(true);
  };

  // ---- Window resizing ----------------------------------------------
  // Header buttons
  const toggleMaximize = () => setMode((m) => (m === "maximized" ? "normal" : "maximized"));
  const toggleMinimize = () => setMode((m) => (m === "minimized" ? "normal" : "minimized"));

  // Corner handle (top-left): the window is anchored bottom-right, so dragging
  // the top-left corner up/left makes it bigger and down/right makes it smaller.
  const onResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = paperRef.current?.getBoundingClientRect();
    if (!rect) return;
    resizeRef.current = { active: true, sx: e.clientX, sy: e.clientY, w: rect.width, h: rect.height };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setSize({ w: rect.width, h: rect.height });
    setMode("normal");
    setResizing(true);
  };

  const onResizeMove = (e) => {
    const r = resizeRef.current;
    if (!r.active) return;
    const w = Math.min(window.innerWidth - 32, Math.max(MIN_W, r.w - (e.clientX - r.sx)));
    const h = Math.min(window.innerHeight - 32, Math.max(MIN_H, r.h - (e.clientY - r.sy)));
    setSize({ w, h });
  };

  const onResizeEnd = () => {
    resizeRef.current.active = false;
    setResizing(false);
  };

  const pushMessage = (msg) => setMessages((prev) => [...prev, msg]);

  // Single exit point for "open a page from inside the chat": navigate, then
  // close the chat. Pass it down as `onNavigate` and render links with
  // <ChatLink to=... onNavigate={goTo} /> to reuse it anywhere.
  const navigate = useNavigate();
  const goTo = (path) => {
    navigate(path);
    setOpen(false);
  };

  // One router for chips, report-menu options and typed text.
  const runAction = (action) => {
    if (action === "ADMIN_HOTEL_DETAILS") {
      const { text, link } = buildHotelInfoMessage(hotelDetails);
      pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "text", text, link });
    } else if (action === "ADMIN_RESERVATION") {
      // Starting a fresh reservation search clears any earlier form/result
      // so the thread only ever shows the current search, not a growing
      // stack of old searches every time "Reservation" is tapped again.
      setMessages((prev) => prev.filter((m) => m.kind !== "dateForm" && m.kind !== "reservationList"));
      pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "dateForm" });
    } else if (action === "ADMIN_REPORT_MENU") {
      setMessages((prev) => [
        ...prev.filter((m) => m.kind !== "reportMenu"),
        { id: crypto.randomUUID(), role: "bot", kind: "reportMenu" },
      ]);
    } else if (CHART_REPORT_KEYS.has(action)) {
      // Chart reports get the chatbot-style date shortcut picker instead of
      // the generic form, so the conversation feels like a real chat.
      setMessages((prev) => [
        ...prev.filter((m) => !(m.kind === "chartDatePicker" && m.reportKey === action)),
        { id: crypto.randomUUID(), role: "bot", kind: "chartDatePicker", reportKey: action },
      ]);
    } else if (REPORTS[action]) {
      // any registered report: date form (single/range) → dialog; "none" opens directly
      if (REPORTS[action].dateMode === "none") loadReport(action, {});
      else pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "reportDateForm", reportKey: action });
    }
  };

  const handleChipClick = (qa) => runAction(qa.action);

  const handleTypedSend = () => {
    const content = input.trim();
    if (!content) return;
    pushMessage({ id: crypto.randomUUID(), role: "user", kind: "text", text: content });
    setInput("");
    const action = matchIntent(content);
    if (action) runAction(action);
    else pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "text", text: FALLBACK_TYPED_MESSAGE });
  };

  // ---- Report dialog -------------------------------------------------
  const loadReport = async (key, params) => {
    const cfg = REPORTS[key];
    if (!cfg || !params) return;
    const reqId = ++reportReq.current;
    // selfLoading view (e.g. the real report page) fetches by itself
    if (cfg.selfLoading) {
      setReportDialog({ open: true, key, params, data: null, loading: false, error: "" });
      return;
    }
    setReportDialog({ open: true, key, params, data: null, loading: true, error: "" });
    try {
      const data = await cfg.fetchData(params);
      if (reqId !== reportReq.current) return;
      setReportDialog((d) => ({ ...d, data, loading: false }));
    } catch (error) {
      console.error("FETCH REPORT ERROR:", error);
      if (reqId !== reportReq.current) return;
      setReportDialog((d) => ({ ...d, loading: false, error: "Couldn't load this report — please try again." }));
    }
  };

  const closeReportDialog = () => {
    reportReq.current++;
    setReportDialog((d) => ({ ...d, open: false }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleTypedSend();
    }
  };

  // filters: { startDate, endDate, searchText, searchById, bookingStatus }
  const handleReservationSearch = async (filters) => {
    if (!filters?.startDate || !filters?.endDate || loading) return;
    setLoading(true);
    try {
      const { rows, totalRows } = await fetchReservationsPage({ ...filters, pageNo: 1 });
      // Replace any earlier result instead of stacking a new one below it —
      // the form (kind "dateForm") stays so the manager can search again.
      setMessages((prev) => [
        ...prev.filter((m) => m.kind !== "reservationList"),
        { id: crypto.randomUUID(), role: "bot", kind: "reservationList", ...filters, rows, totalRows, pageNo: 1 },
      ]);
      setLastSearchFilters(filters);
      if (rows[0]) setExpandedId(rows[0].id);
    } catch (error) {
      console.error("FETCH RESERVATIONS ERROR:", error);
      pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "text", text: "I couldn't load reservations — please try again." });
    } finally {
      setLoading(false);
    }
  };

  // msg already carries the filters it was searched with
  const handleLoadMore = async (msg) => {
    if (loading) return;
    setLoading(true);
    try {
      const nextPage = msg.pageNo + 1;
      const { startDate, endDate, searchText, searchById, bookingStatus } = msg;
      const { rows: newRows, totalRows } = await fetchReservationsPage({
        startDate, endDate, searchText, searchById, bookingStatus, pageNo: nextPage,
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, rows: [...m.rows, ...newRows], pageNo: nextPage, totalRows } : m))
      );
    } catch (error) {
      console.error("LOAD MORE RESERVATIONS ERROR:", error);
    } finally {
      setLoading(false);
    }
  };

  // ---- Check In / Check Out dialog -----------------------------------
  // Same as ReservationView.js's handleEdit(): the reservation-list row
  // doesn't carry enough data (no bookingItemsData.lockDetail,
  // bookingPaymentDetails, hotelData, agreementDetails, full userData...),
  // so this fetches the full booking detail first, THEN opens the dialog
  // with the real ReservationCheckIn / ReservationCheckOut component.
  const openCheckInOut = async (bookingId, mode) => {
    setCheckDialog({ open: true, mode, editedData: null, loading: true });
    try {
      const result = await fetchList(`${ApiEndPoints.GET_BOOKING_DETAILS}/${bookingId}`);
      if (result?.status !== 200) {
        setCheckDialog({ open: false, mode: null, editedData: null, loading: false });
        pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "text", text: "I couldn't load that booking's details — please try again." });
        return;
      }
      setCheckDialog({ open: true, mode, editedData: result.data, loading: false });
      // Virtual-card token — same su_pg_id check handleEdit() does.
      if (result?.data?.su_pg_id) {
        setSuToken(result.data.su_pg_id);
        setSuOn(true);
      } else {
        setSuToken(null);
        setSuOn(false);
      }
    } catch (error) {
      console.error("FETCH BOOKING DETAIL ERROR:", error);
      setCheckDialog({ open: false, mode: null, editedData: null, loading: false });
      pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "text", text: "I couldn't load that booking's details — please try again." });
    }
  };

  const closeCheckInOut = () => {
    setCheckDialog({ open: false, mode: null, editedData: null, loading: false });
    // Re-run the last search so the status column reflects the change —
    // same role getReservationList() plays on the real grid.
    if (lastSearchFilters) handleReservationSearch(lastSearchFilters);
  };

  // ---- Chart report load (Revenue By Source / Transaction Batch / OTA) ---
  // Called when the user picks a date shortcut or submits custom dates.
  // Pushes the result inline into the chat as kind:"chartResult" so it
  // renders inside the message thread — no dialog needed, feels native.
  const loadChartReport = async (reportKey, startDate, endDate) => {
    setLoading(true);
    try {
      const fetcher =
        reportKey === "ADMIN_REVENUE_BY_SOURCE" ? fetchRevenueBySource
        : reportKey === "ADMIN_TRANSACTION_BATCH" ? fetchTransactionBatch
        : fetchOtaCommission;

      const data = await fetcher({ startDate, endDate });
      setMessages((prev) => [
        // Drop any stale result for this same report key
        ...prev.filter((m) => !(m.kind === "chartResult" && m.reportKey === reportKey)),
        {
          id: crypto.randomUUID(),
          role: "bot",
          kind: "chartResult",
          reportKey,
          ...data,
          startDate,
          endDate,
        },
      ]);
    } catch (error) {
      console.error("CHART REPORT ERROR:", error);
      pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "text", text: "I couldn't load that report — please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Chat window size — anchored bottom-right. Minimized keeps the width but
  // collapses to just the header bar; maximized fills most of the screen.
  const baseW = size ? `${size.w}px` : { xs: "calc(100vw - 32px)", sm: DEFAULT_W };
  const WINDOW_W = expanded ? `min(${MAX_W}px, calc(100vw - 48px))` : baseW;
  const WINDOW_H = minimized
    ? HEADER_H
    : expanded
    ? "calc(100vh - 48px)"
    : size
    ? `${size.h}px`
    : { xs: "82vh", sm: `min(${DEFAULT_H}px, calc(100vh - 96px))` };
  // animate size changes from the toggle button, but not while dragging the handle
  const sizeTransition = resizing ? "" : ", width 260ms ease, height 260ms ease";

  return (
    <>
      {/* Reusable report dialog — body/print come from reportRegistry */}
      <ReportDialogHost
        state={reportDialog}
        printRef={printRef}
        onClose={closeReportDialog}
        onRetry={() => loadReport(reportDialog.key, reportDialog.params)}
        primaryColor={primaryColor}
        accentColor={accentColor}
      />

      {/* Check In / Check Out — the actual ReservationCheckIn/Out components,
          same as the Reservation grid opens, just fed from openCheckInOut() above. */}
      <DialogForm
        scroll="paper"
        maxWidth="lg"
        title={checkDialog.mode === "checkout" ? "Select check-out method" : "Confirm Check In"}
        openDialog={checkDialog.open}
        handleDialogClose={closeCheckInOut}
        bodyContent={
          checkDialog.loading || !checkDialog.editedData ? (
            <Loader />
          ) : checkDialog.mode === "checkout" ? (
            <ReservationCheckOut
              handleDialogOutClose={closeCheckInOut}
              refreshCheckInOutListCall={closeCheckInOut}
              editedData={checkDialog.editedData}
              isCashAllowed={isCashAllowed}
            />
          ) : (
            <ReservationCheckIn
              handleDialogClose={closeCheckInOut}
              refreshCheckInOutListCall={closeCheckInOut}
              editedData={checkDialog.editedData}
              suToken={suToken}
              suOn={suOn}
              handleSuOn={(e) => setSuOn(e.target.checked)}
              setOpenMakePaymentDialog={setOpenMakePaymentDialog}
              setShowPaymentMessage={setShowPaymentMessage}
            />
          )
        }
      />
      <MakePaymentDialog
        open={openMakePaymentDialog}
        onClose={() => setOpenMakePaymentDialog(false)}
        bookingId={checkDialog.editedData?.id}
        bookingDetail={checkDialog.editedData}
        showNote={showPaymentMessage}
        onSuccess={() => {
          // refreshes editedData shown in the check-in form, same as the grid's onSuccess
          if (checkDialog.editedData?.id) openCheckInOut(checkDialog.editedData.id, "checkin");
        }}
      />

      {/* Ambient glow behind the panel while open */}
      {mounted && (
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            width: WINDOW_W,
            height: WINDOW_H,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 32px)",
            transition: "width 260ms ease, height 260ms ease",
            borderRadius: 4,
            background: `radial-gradient(60% 60% at 50% 50%, ${accentColor}40 0%, transparent 70%)`,
            filter: "blur(28px)",
            transform: "scale(1.08)",
            opacity: open ? 1 : 0,
            transition: `opacity ${open ? OPEN_MS : CLOSE_MS}ms ease`,
            pointerEvents: "none",
            zIndex: 1299,
          }}
        />
      )}

      {/* Chat Window — fixed, bottom-right of the viewport */}
      {mounted && (
        <Paper
          ref={paperRef}
          elevation={0}
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            width: WINDOW_W,
            height: WINDOW_H,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 32px)",
            display: "flex",
            flexDirection: "column",
            borderRadius: 4,
            overflow: "hidden",
            zIndex: 1300,
            transformOrigin: "bottom right",
            boxShadow: "0 24px 48px -12px rgba(15,23,42,0.28), 0 8px 20px -4px rgba(15,23,42,0.12)",
            border: "1px solid rgba(15,23,42,0.06)",
            clipPath: open ? "circle(150% at 100% 100%)" : "circle(0% at 100% 100%)",
            transform: open ? "translateY(0) scale(1)" : "translateY(10px) scale(0.96)",
            transition: open
              ? `clip-path ${OPEN_MS}ms cubic-bezier(0.16, 1, 0.3, 1), transform ${OPEN_MS}ms cubic-bezier(0.16, 1, 0.3, 1)${sizeTransition}`
              : `clip-path ${CLOSE_MS}ms cubic-bezier(0.7, 0, 0.84, 0), transform ${CLOSE_MS}ms ease-in`,
            pointerEvents: open ? "auto" : "none",
          }}
        >
          {/* Header — click it while minimized to restore the window */}
          <Box
            onClick={minimized ? () => setMode("normal") : undefined}
            style={{ cursor: minimized ? "pointer" : "default" }}
            sx={{
              px: 2.5,
              py: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
              color: "#fff",
              flexShrink: 0,
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0)" : "translateY(-6px)",
              transition: `opacity 240ms ease ${open ? 120 : 0}ms, transform 240ms ease ${open ? 120 : 0}ms`,
            }}
          >
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.25)" }}>
              <SupportAgentRoundedIcon />
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap letterSpacing={0.2}>
                {hotelName}
              </Typography>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#22c55e",
                    boxShadow: "0 0 0 3px rgba(34,197,94,0.25)",
                  }}
                />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Online
                </Typography>
              </Stack>
            </Box>
            <Tooltip title={minimized ? "Restore" : "Minimize"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMinimize();
                }}
                sx={{
                  color: "#fff",
                  transition: "background-color 150ms ease",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                }}
              >
                <MinimizeRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={expanded ? "Restore" : "Maximize"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMaximize();
                }}
                sx={{
                  color: "#fff",
                  transition: "background-color 150ms ease",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                }}
              >
                {expanded ? <CloseFullscreenRoundedIcon fontSize="small" /> : <OpenInFullRoundedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{
                color: "#fff",
                transition: "background-color 150ms ease, transform 150ms ease",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)", transform: "rotate(90deg)" },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            ref={scrollRef}
            sx={{
              flexGrow: 1,
              position: "relative",
              overflowY: "auto",
              overflowX: "hidden",
              px: 2,
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              bgcolor: "#f4f6fb",
              scrollBehavior: "smooth",
              opacity: open ? 1 : 0,
              transition: `opacity 260ms ease ${open ? 180 : 0}ms`,
            }}
          >
            {messages.map((m) =>
              m.kind === "dateForm" ? (
                <AnimatedEntry key={m.id} msgId={m.id}>
                  <DateRangeFormMessage accentColor={accentColor} onSearch={handleReservationSearch} />
                </AnimatedEntry>
              ) : m.kind === "reportMenu" ? (
                <AnimatedEntry key={m.id}>
                  <ReportMenuMessage accentColor={accentColor} onPick={runAction} />
                </AnimatedEntry>
              ) : m.kind === "reportDateForm" ? (
                <AnimatedEntry key={m.id}>
                  <ReportDateFormMessage cfg={REPORTS[m.reportKey]} onApply={(params) => loadReport(m.reportKey, params)} />
                </AnimatedEntry>
              ) : m.kind === "reservationList" ? (
                <AnimatedEntry key={m.id} msgId={m.id}>
                  <ReservationListMessage
                    msg={m}
                    accentColor={accentColor}
                    expandedId={expandedId}
                    setExpandedId={setExpandedId}
                    onLoadMore={handleLoadMore}
                    loadingMore={loading}
                    onCheckIn={(id) => openCheckInOut(id, "checkin")}
                    onCheckOut={(id) => openCheckInOut(id, "checkout")}
                    onNavigate={goTo}
                  />
                </AnimatedEntry>
              ) : m.kind === "chartDatePicker" ? (
                <AnimatedEntry key={m.id}>
                  <ChartDatePickerMessage
                    reportKey={m.reportKey}
                    accentColor={accentColor}
                    onApply={loadChartReport}
                  />
                </AnimatedEntry>
              ) : m.kind === "chartResult" ? (
                <AnimatedEntry key={m.id} msgId={m.id}>
                  <ChartResultMessage msg={m} accentColor={accentColor} onReselect={() => runAction(m.reportKey)} />
                </AnimatedEntry>
              ) : (
                <MessageBubble key={m.id} message={m} accentColor={accentColor} />
              )
            )}

            {loading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pl: 0.5,
                  animation: "chatbot-fade-in 200ms ease-out",
                  "@keyframes chatbot-fade-in": {
                    from: { opacity: 0, transform: "translateY(4px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              >
                <Avatar sx={{ width: 26, height: 26, bgcolor: accentColor }}>
                  <SmartToyRoundedIcon sx={{ fontSize: 15 }} />
                </Avatar>
                <Box sx={{ bgcolor: "#fff", borderRadius: "14px 14px 14px 4px", px: 1.75, py: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                  <TypingDots />
                </Box>
              </Box>
            )}

            {/* Quick-action chips — always visible, same style as the guest bot */}
            {!loading && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, fontWeight: 600 }}>
                  Quick actions
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.75 }}>
                  {QUICK_ACTIONS.map((qa) => (
                    <Chip
                      key={qa.label}
                      label={qa.label}
                      size="small"
                      onClick={() => handleChipClick(qa)}
                      sx={{
                        bgcolor: "#fff",
                        border: "1px solid #e2e5ec",
                        fontWeight: 500,
                        transition: "all 150ms ease",
                        "&:hover": { bgcolor: "#eef2ff", borderColor: accentColor, transform: "translateY(-1px)" },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          {/* Input */}
          <Box
            sx={{
              p: 1.5,
              borderTop: "1px solid #eee",
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              bgcolor: "#fff",
              flexShrink: 0,
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0)" : "translateY(6px)",
              transition: `opacity 260ms ease ${open ? 220 : 0}ms, transform 260ms ease ${open ? 220 : 0}ms`,
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              inputRef={inputRef}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "#f5f6fa",
                  transition: "background-color 150ms ease",
                  "&.Mui-focused": { bgcolor: "#fff" },
                },
              }}
            />
            <Tooltip title="Send">
              <span>
                <IconButton
                  onClick={handleTypedSend}
                  disabled={loading || !input.trim()}
                  sx={{
                    bgcolor: accentColor,
                    color: "#fff",
                    transition: "all 150ms ease",
                    "&:hover": { bgcolor: accentColor, opacity: 0.9, transform: "scale(1.05)" },
                    "&.Mui-disabled": { bgcolor: "#e2e5ec", color: "#aaa" },
                  }}
                >
                  <SendRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {/* Top-left corner drag handle — resize the window by dragging (normal mode only) */}
          {mode === "normal" && (
            <Box
              onPointerDown={onResizeStart}
              onPointerMove={onResizeMove}
              onPointerUp={onResizeEnd}
              onPointerCancel={onResizeEnd}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 22,
                height: 22,
                cursor: "nwse-resize",
                touchAction: "none",
                userSelect: "none",
                zIndex: 3,
                opacity: 0.8,
                background:
                  "linear-gradient(315deg, transparent 0 55%, #cbd5e1 55% 62%, transparent 62% 74%, #cbd5e1 74% 81%, transparent 81%)",
                "&:hover": { opacity: 1 },
              }}
            />
          )}
        </Paper>
      )}

      {/* Launcher — draggable, hidden while the chat is open, shown again when it closes */}
      {!open && (
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: 1300,
            transform: `translate(${pos.x}px, ${pos.y}px)`,
          }}
        >
          <Tooltip title="Manager chat (drag to move)" placement="top">
            <Box
              sx={{
                position: "relative",
                width: LAUNCHER,
                height: LAUNCHER,
                animation: "chatbot-launcher-in 320ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                "@keyframes chatbot-launcher-in": {
                  from: { opacity: 0, transform: "scale(0.4)" },
                  to: { opacity: 1, transform: "scale(1)" },
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  bgcolor: accentColor,
                  opacity: 0.35,
                  pointerEvents: "none",
                  animation: "chatbot-pulse 2.4s ease-out infinite",
                  "@keyframes chatbot-pulse": {
                    "0%": { transform: "scale(1)", opacity: 0.35 },
                    "70%": { transform: "scale(1.45)", opacity: 0 },
                    "100%": { transform: "scale(1.45)", opacity: 0 },
                  },
                }}
              />
              <IconButton
                onPointerDown={onDragStart}
                onPointerMove={onDragMove}
                onPointerUp={onDragEnd}
                onPointerCancel={onDragEnd}
                onClick={handleLauncherClick}
                sx={{
                  position: "relative",
                  width: LAUNCHER,
                  height: LAUNCHER,
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                  color: "#fff",
                  cursor: "grab",
                  touchAction: "none",
                  userSelect: "none",
                  boxShadow: "0 10px 28px rgba(37,99,235,0.4)",
                  transition: "transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 220ms ease",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                    transform: "scale(1.06)",
                  },
                  "&:active": { transform: "scale(0.94)", cursor: "grabbing" },
                }}
              >
                {/* Concierge bell icon */}
                <SvgIcon viewBox="55 0 410 500" sx={{ fontSize: 30, pointerEvents: "none" }}>
                  <path d="M 245 10 L 230 13 L 209 22 L 197 31 L 188 41 L 178 59 L 174 72 L 172 83 L 172 111 L 177 136 L 183 153 L 191 169 L 203 186 L 215 198 L 235 210 L 249 214 L 265 214 L 279 210 L 297 199 L 311 185 L 321 171 L 330 154 L 337 134 L 342 107 L 342 87 L 339 70 L 331 50 L 322 37 L 310 26 L 292 16 L 275 11 Z" />
                  <path d="M 145 60 L 129 68 L 103 86 L 66 122 L 40 160 L 27 187 L 19 210 L 10 257 L 9 286 L 11 312 L 15 334 L 23 362 L 32 384 L 42 402 L 51 330 L 51 321 L 47 301 L 46 273 L 49 244 L 55 219 L 63 197 L 73 177 L 89 153 L 118 122 L 141 105 L 141 81 Z" />
                  <path d="M 368 61 L 372 76 L 373 106 L 394 122 L 414 142 L 426 157 L 440 179 L 457 219 L 465 259 L 466 283 L 463 318 L 472 399 L 491 356 L 501 311 L 503 282 L 502 258 L 493 210 L 482 180 L 472 160 L 446 122 L 409 86 L 383 68 Z" />
                  <path d="M 87 290 L 68 453 L 68 472 L 75 479 L 106 488 L 153 494 L 297 497 L 409 487 L 435 480 L 446 472 L 431 301 L 424 282 L 411 270 L 338 249 L 286 396 L 280 325 L 259 297 L 278 278 L 283 265 L 266 245 L 259 242 L 249 245 L 232 265 L 234 274 L 255 297 L 234 322 L 228 397 L 176 250 L 112 268 L 99 276 Z" />
                  <g transform="translate(328 10) scale(1.15)">
                    <path
                      d="M40 20h100c11 0 20 9 20 20v58c0 11-9 20-20 20H92l-30 25v-25H40c-11 0-20-9-20-20V40c0-11 9-20 20-20Z"
                      fill="currentColor"
                    />
                    <circle cx="65" cy="69" r="6" fill="white" />
                    <circle cx="90" cy="69" r="6" fill="white" />
                    <circle cx="115" cy="69" r="6" fill="white" />
                  </g>
                </SvgIcon>
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      )}
    </>
  );
}

// ---------------------------------------------------------------------
// Shared entrance animation wrapper — same keyframe the guest bot uses
// for every message bubble, reused here for the date form and the
// reservation list so everything entering the thread animates the
// same way.
// ---------------------------------------------------------------------
function AnimatedEntry({ children, msgId }) {
  return (
    <Box
      data-msg-id={msgId}
      sx={{
        boxSizing: "border-box",
        minWidth: 0,
        animation: "chatbot-msg-in 240ms cubic-bezier(0.22, 1, 0.36, 1)",
        "@keyframes chatbot-msg-in": {
          from: { opacity: 0, transform: "translateY(6px) scale(0.98)" },
          to: { opacity: 1, transform: "translateY(0) scale(1)" },
        },
      }}
    >
      {children}
    </Box>
  );
}

// A link inside the chat that opens a page AND closes the chat. `onNavigate`
// is the widget's goTo(); a normal click goes through it, while
// Ctrl/Cmd/Shift/middle-click still open a new tab the usual way.
// stopRowToggle: set it when the link sits inside an accordion header.
function ChatLink({ to, onNavigate, children, style, stopRowToggle }) {
  return (
    <a
      href={to}
      onClick={(e) => {
        if (stopRowToggle) e.stopPropagation();
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        onNavigate(to);
      }}
      style={style}
    >
      {children}
    </a>
  );
}

// ---------------------------------------------------------------------
// Report menu — list of reports after "Report" is clicked
// ---------------------------------------------------------------------
function ReportMenuMessage({ onPick, accentColor }) {
  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px 16px 16px 4px", p: 1.75, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", maxWidth: "92%" }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Which report would you like?</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {REPORT_OPTIONS.map((r) => (
          <Chip
            key={r.action}
            label={r.label}
            size="small"
            onClick={() => onPick(r.action)}
            sx={{
              bgcolor: "#fff",
              border: "1px solid #e2e5ec",
              fontWeight: 500,
              transition: "all 150ms ease",
              "&:hover": { bgcolor: "#eef2ff", borderColor: accentColor, transform: "translateY(-1px)" },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Generic report date form — driven by REPORTS[x].dateMode ("single" | "range").
// Stays editable so the manager can re-apply another date.
// ---------------------------------------------------------------------
function ReportDateFormMessage({ cfg, onApply }) {
  const range = cfg?.dateMode === "range";
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(range ? addDaysISO(7) : todayISO());
  const invalid = !start || (range && (!end || end < start));

  const apply = () => {
    if (invalid) return;
    onApply(range ? { startDate: start, endDate: end } : { date: start });
  };

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px 16px 16px 4px", p: 1.75, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", maxWidth: "92%" }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
        {cfg?.label} — select {range ? "a date range" : "a date"}
      </Typography>
      <Stack direction="row" gap={1}>
        <TextField type="date" size="small" label={range ? "From" : "Date"} value={start} onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        {range && (
          <TextField type="date" size="small" label="To" value={end} onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        )}
      </Stack>
      <Button fullWidth variant="contained" disableElevation sx={{ mt: 1.25 }} onClick={apply} disabled={invalid}>
        Apply
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Binds the reusable ReportDialog to a registry entry (body + optional print).
// ---------------------------------------------------------------------
function ReportDialogHost({ state, printRef, onClose, onRetry, primaryColor, accentColor }) {
  const cfg = state.key ? REPORTS[state.key] : null;
  if (!cfg) return null;
  const { View, Print } = cfg;
  const ready = cfg.selfLoading || state.data;

  return (
    <ReportDialog
      open={state.open}
      onClose={onClose}
      title={cfg.label}
      subtitle={reportSubtitle(cfg, state.params)}
      loading={state.loading}
      error={state.error}
      onRetry={onRetry}
      primaryColor={primaryColor}
      accentColor={accentColor}
      actions={
        Print && state.data ? (
          <ReactToPrint
            trigger={() => (
              <Button size="small" variant="outlined" sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)", mr: 0.5 }}>
                Print
              </Button>
            )}
            content={() => printRef.current}
            documentTitle={cfg.printTitle?.(state.params)}
            pageStyle={`@page { size: A4; margin: 11mm 8mm 17mm 8mm; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }`}
          />
        ) : null
      }
    >
      {ready && <View params={state.params} data={state.data} />}
      {Print && state.data && (
        <div style={{ display: "none" }}>
          <Print ref={printRef} reportData={state.data} date={cfg.printDate?.(state.params)} t={(s) => s} />
        </div>
      )}
    </ReportDialog>
  );
}

// ---------------------------------------------------------------------
// Reservation search form — dates + search text + "by Booking ID" toggle + status.
// Stays editable so the manager can run several searches.
// ---------------------------------------------------------------------
function DateRangeFormMessage({ onSearch, accentColor }) {
  const [text, setText] = useState("");
  const [byId, setById] = useState(false);
  const [status, setStatus] = useState(0);

  // PeriodPicker calls this when a period chip (or Custom -> Search) is chosen,
  // so picking a period runs the search with whatever filters are set above.
  const search = (periodLabel, startDate, endDate) => {
    let searchText = text.trim();
    // booking ids are shown as "#000123" — the API wants the plain id
    if (byId) searchText = searchText.replace(/^#?0*/, "");
    onSearch({ startDate, endDate, periodLabel, searchText, searchById: byId && !!searchText, bookingStatus: status });
  };

  const fieldLabel = (t) => (
    <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
      {t}
    </Typography>
  );
  const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#f8fafc" } };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        width: "100%",
        maxWidth: 520,
        bgcolor: "#fff",
        borderRadius: "16px 16px 16px 4px",
        p: 1.5,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.75} mb={1.25}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: accentColor, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Search Reservations</Typography>
      </Stack>

      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        <TextField
          size="small"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={byId ? "Booking ID e.g. 123" : "Guest name or mobile…"}
          inputProps={byId ? { inputMode: "numeric" } : undefined}
          sx={{ flex: 1, minWidth: 0, ...inputSx }}
        />
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
          <Switch size="small" checked={byId} onChange={(e) => setById(e.target.checked)} sx={{ m: 0 }} />
          <Typography sx={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>By ID</Typography>
        </Stack>
      </Stack>

      <TextField select size="small" fullWidth value={status} onChange={(e) => setStatus(e.target.value)} sx={{ mb: 1.25, ...inputSx }}>
        <MenuItem value={0} sx={{ fontSize: 13 }}>All statuses</MenuItem>
        {BOOKING_STATUS_OPTIONS.map((s) => (
          <MenuItem key={s.id} value={s.id} sx={{ fontSize: 13 }}>
            {s.name}
          </MenuItem>
        ))}
      </TextField>

      {fieldLabel("Check-in period · tap one to search")}
      <PeriodPicker
        presetIds={["thisWeek", "prevWeek", "nextWeek", "nextMonth"]}
        applyLabel="Search"
        accentColor={accentColor}
        onApply={search}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------
// Collapsible, paginated reservation list
// ---------------------------------------------------------------------
function ReservationListMessage({ msg, accentColor, expandedId, setExpandedId, onLoadMore, loadingMore, onCheckIn, onCheckOut, onNavigate }) {
  const hasMore = msg.rows.length < msg.totalRows;
  console.log(msg);
  const statusName = BOOKING_STATUS_OPTIONS.find((s) => s.id === msg.bookingStatus)?.name;
  const filterChips = [
    `Status: ${statusName || "All"}`,
    msg.searchText && `${msg.searchById ? "Booking ID" : "Search"}: ${msg.searchText}`,
  ].filter(Boolean);

  // Status colour map — matches the real grid's visual language
  const statusColor = (status = "") => {
    const s = status.toLowerCase();
    if (s === "confirmed") return { bg: "#dbeafe", color: "#1d4ed8" };
    if (s === "in house") return { bg: "#dcfce7", color: "#15803d" };
    if (s === "checked-out") return { bg: "#f1f5f9", color: "#475569" };
    if (s === "cancelled") return { bg: "#fee2e2", color: "#b91c1c" };
    if (s === "no show") return { bg: "#fef3c7", color: "#92400e" };
    if (s === "pending") return { bg: "#fef9c3", color: "#a16207" };
    return { bg: "#e2e8f0", color: "#334155" };
  };

  return (
    <Box sx={{ boxSizing: "border-box", width: "100%", maxWidth: 760 }}>
      {/* Summary: count, period, and every filter that was applied */}
      <Box
        sx={{
          boxSizing: "border-box",
          width: "100%",
          bgcolor: "#fff",
          borderRadius: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          px: 2,
          py: 1.25,
          mb: 1.25,
          textAlign: "center",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="center" gap={0.75}>
          <Box
            sx={{
              minWidth: 22,
              height: 22,
              px: 0.75,
              boxSizing: "border-box",
              borderRadius: 11,
              bgcolor: accentColor,
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {msg.totalRows}
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
            reservation{msg.totalRows === 1 ? "" : "s"}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
          {msg.periodLabel ? `${msg.periodLabel} · ` : ""}
          {msg.startDate} → {msg.endDate}
        </Typography>
        <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={0.75} mt={0.9}>
          {filterChips.map((c) => (
            <Chip key={c} label={c} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569" }} />
          ))}
        </Stack>
      </Box>

      {msg.rows.length === 0 && (
        <Typography sx={{ fontSize: 13, color: "#94a3b8", textAlign: "center", py: 2 }}>
          No reservations match these filters.
        </Typography>
      )}

      <Stack gap={0.75}>
        {msg.rows.map((r) => {
          const bookingLink = `/reservation/bookingDetail/${cryptoEncryptData(String(r.id))}`;
          // Same condition as the Reservation grid's Actions column: Check In only
          // when Confirmed (status id 2), Check Out only when In House (3).
          // r.statusId comes from normalizeReservation() in chatbotApi.js.
          const canCheckIn = r.status === "Confirmed";
          const canCheckOut = r.status === "In House";

          return (
            <Accordion
              key={r.id}
              disableGutters
              expanded={expandedId === r.id}
              onChange={() => setExpandedId(expandedId === r.id ? null : r.id)}
              sx={{
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                "&:before": { display: "none" },
                borderRadius: "12px !important",
                overflow: "hidden",
                border: "1px solid #f1f5f9",
                transition: "box-shadow 150ms ease",
                "&:hover": { boxShadow: "0 3px 8px rgba(0,0,0,0.10)" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
                sx={{ minHeight: 48, px: 1.5, "& .MuiAccordionSummary-content": { my: 0.75, minWidth: 0 } }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, width: "100%", minWidth: 0, pr: 0.5 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <ChatLink
                      to={bookingLink}
                      onNavigate={onNavigate}
                      stopRowToggle
                      style={{ fontSize: 13, fontWeight: 700, color: accentColor, textDecoration: "none" }}
                    >
                      {r.bookingId}
                    </ChatLink>
                    <Typography component="span" sx={{ fontSize: 13, color: "#475569", ml: 0.75 }}>
                      · {r.name}
                    </Typography>
                  </Box>
                  <Chip
                    label={r.status}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      bgcolor: statusColor(r.status).bg,
                      color: statusColor(r.status).color,
                      height: 22,
                      fontSize: 11,
                      fontWeight: 600,
                      border: "none",
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <DetailRow
                  label="Booking ID"
                  value={
                    <ChatLink to={bookingLink} onNavigate={onNavigate} style={{ color: accentColor, textDecoration: "none" }}>
                      {r.bookingId}
                    </ChatLink>
                  }
                />
                <DetailRow label="Guest Name" value={r.name} />
                {/* <DetailRow label="Phone Number" value={r.phone} /> */}
                {/* <DetailRow label="Room No" value={r.bookingItemsData?.[0]?.room_no} /> */}
                <DetailRow label="Check-In Date" value={r.checkin} />
                <DetailRow label="Check-Out Date" value={r.checkout} />
                <DetailRow label="Booking Status" value={r.status} />
                <DetailRow label="No. Of Days" value={r.nights} />
                <DetailRow label="Final Booking Amount" value={r.amount} />
                <DetailRow label="Room Type" value={r.roomType} last={!canCheckIn && !canCheckOut} />

                {(canCheckIn || canCheckOut) && (
                  <Stack direction="row" gap={1} sx={{ mt: 1.25 }}>
                    {canCheckIn && (
                      <Button size="small" variant="contained" disableElevation fullWidth onClick={() => onCheckIn(r.id)}>
                        Check In
                      </Button>
                    )}
                    {canCheckOut && (
                      <Button size="small" variant="contained" disableElevation fullWidth onClick={() => onCheckOut(r.id)}>
                        Check Out
                      </Button>
                    )}
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      {hasMore && (
        <Button
          fullWidth
          size="small"
          onClick={() => onLoadMore(msg)}
          disabled={loadingMore}
          sx={{ mt: 1, textTransform: "none", fontSize: 12, color: accentColor, bgcolor: "#eff6ff", borderRadius: 2, "&:hover": { bgcolor: "#dbeafe" } }}
        >
          {loadingMore ? "Loading…" : `Show more · ${msg.rows.length} of ${msg.totalRows}`}
        </Button>
      )}
    </Box>
  );
}

function DetailRow({ label, value, last }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        py: 0.75,
        borderBottom: last ? "none" : "1px solid #f1f5f9",
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a", textAlign: "right", wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Message bubble + structured-text formatting — identical to the guest
// bot's version, duplicated here (not imported) to keep the two bots
// fully independent files.
// ---------------------------------------------------------------------
function MessageBubble({ message, accentColor }) {
  const isUser = message.role === "user";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        gap: 1,
        animation: "chatbot-msg-in 240ms cubic-bezier(0.22, 1, 0.36, 1)",
        "@keyframes chatbot-msg-in": {
          from: { opacity: 0, transform: "translateY(6px) scale(0.98)" },
          to: { opacity: 1, transform: "translateY(0) scale(1)" },
        },
      }}
    >
      {!isUser && (
        <Avatar sx={{ width: 26, height: 26, bgcolor: accentColor, mt: 0.3, flexShrink: 0 }}>
          <SmartToyRoundedIcon sx={{ fontSize: 15 }} />
        </Avatar>
      )}
      <Box sx={{ maxWidth: "82%" }}>
        <Box
          sx={{
            bgcolor: isUser ? accentColor : "#fff",
            color: isUser ? "#fff" : "#1e293b",
            px: 1.75,
            py: isUser ? 1 : 1.25,
            borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          {isUser ? (
            <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{message.text}</Box>
          ) : (
            <FormattedBotText text={message.text} />
          )}
        </Box>

        {!isUser && message.link && (
          <Box sx={{ mt: 0.75 }}>
            <Chip
              component="a"
              href={message.link.url}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              size="small"
              icon={<RoomRoundedIcon sx={{ fontSize: 16, color: "#fff !important" }} />}
              label={message.link.label}
              sx={{
                bgcolor: accentColor,
                color: "#fff",
                fontWeight: 500,
                transition: "all 150ms ease",
                "&:hover": { bgcolor: accentColor, opacity: 0.9, transform: "translateY(-1px)" },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Renders a bot reply as plain text, or — if it follows the
 * "intro line\n• Label: value\n..." shape — as a heading + bullet list.
 * buildHotelInfoMessage() in chatbotApi.js returns exactly that shape.
 */
function FormattedBotText({ text }) {
  if (!text) return null;

  const lines = text.split("\n").filter((l) => l.trim().length > 0);
  const bulletLines = lines.filter((l) => l.trim().startsWith("•"));

  if (bulletLines.length === 0) {
    return <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{text}</Box>;
  }

  const introLines = lines.filter((l) => !l.trim().startsWith("•"));

  const rows = bulletLines.map((l) => {
    const stripped = l.trim().replace(/^•\s*/, "");
    const colonIdx = stripped.indexOf(":");
    if (colonIdx > -1 && colonIdx < 40) {
      return { label: stripped.slice(0, colonIdx).trim(), value: stripped.slice(colonIdx + 1).trim() };
    }
    return { label: null, value: stripped };
  });

  return (
    <Box>
      {introLines.length > 0 && (
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#0f172a", mb: 0.75, lineHeight: 1.45 }}>
          {introLines.join(" ")}
        </Typography>
      )}
      <Stack gap={0.6}>
        {rows.map((row, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 0.9 }}>
            <FiberManualRecordRoundedIcon sx={{ fontSize: 6, color: "#94a3b8", mt: 0.9, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: "#334155", wordBreak: "break-word" }}>
              {row.label ? (
                <>
                  <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                    {row.label}:
                  </Box>{" "}
                  {row.value}
                </>
              ) : (
                row.value
              )}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------
// ChartDatePickerMessage — chat-style date shortcut picker.
// Preset chips first; "Custom range" expands a compact two-field row.
// After a choice is made it collapses to a one-line summary so the
// thread stays tidy (no big dead form left behind).
// ---------------------------------------------------------------------
function ChartDatePickerMessage({ reportKey, accentColor, onApply }) {
  const label = CHART_REPORT_LABELS[reportKey] || "Report";
  return (
    <Box
      sx={{
        boxSizing: "border-box",
        width: "100%",
        maxWidth: 520,
        bgcolor: "#fff",
        borderRadius: "16px 16px 16px 4px",
        p: 1.5,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.75} mb={0.5}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: accentColor, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{label}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1, ml: 1.75 }}>Which period?</Typography>
      <PeriodPicker
        presetIds={["thisWeek", "prevWeek", "thisMonth", "prevMonth"]}
        applyLabel="Generate"
        accentColor={accentColor}
        onApply={(_, startDate, endDate) => onApply(reportKey, startDate, endDate)}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------
// ChartResultMessage — the chart card inside the thread.
// Fixed, modest height so it never swamps a small chat window; the
// body scrolls if the table is long. Works at min, normal and max size.
// ---------------------------------------------------------------------
function ChartResultMessage({ msg, accentColor, onReselect }) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "16px 16px 16px 4px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
        overflow: "hidden",
        boxSizing: "border-box",
        width: "100%",
        maxWidth: 760,
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #f1f5f9",
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }} noWrap>
            {CHART_REPORT_LABELS[msg.reportKey] || "Report"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>
            {msg.startDate} → {msg.endDate}
          </Typography>
        </Box>
        <Chip
          label="Change dates"
          size="small"
          onClick={onReselect}
          sx={{
            flexShrink: 0,
            bgcolor: "#fff",
            border: "1px solid #e2e8f0",
            fontSize: 11,
            fontWeight: 600,
            height: 24,
            "&:hover": { bgcolor: "#eff6ff", borderColor: accentColor, color: accentColor },
          }}
        />
      </Box>

      <Box>
        <ReportChartView
          rows={msg.rows}
          columns={msg.columns}
          selectedTab={msg.selectedTab}
          summary={msg.summary}
          startDate={msg.startDate}
          endDate={msg.endDate}
        />
      </Box>
    </Box>
  );
}

function TypingDots() {
  return (
    <Stack direction="row" gap={0.5} sx={{ py: 0.25 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: "#94a3b8",
            animation: "chatbot-bounce 1.2s infinite ease-in-out",
            animationDelay: `${i * 0.15}s`,
            "@keyframes chatbot-bounce": {
              "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
              "40%": { transform: "scale(1)", opacity: 1 },
            },
          }}
        />
      ))}
    </Stack>
  );
}